import { isPlatformServer, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { AlertFacade } from '@curbnturf/alert/src/lib/+state/alert.facade';
import { ILatLon } from '@curbnturf/entities';
import { LoadingController } from '@ionic/angular';
import { HereMapComponent } from '../here-map/here-map.component';

const DEFAULT_LAT = 44.094533;
const DEFAULT_LON = -116.74177;

@Component({
  standalone: true,
  selector: 'curbnturf-coordinates-map',
  templateUrl: './coordinates-map.component.html',
  styleUrls: ['./coordinates-map.component.scss'],
  imports: [NgIf, forwardRef(() => HereMapComponent)],
})
export class CoordinatesMapComponent implements OnInit {
  @Output() locationClicked = new EventEmitter<ILatLon>();

  @Output() close = new EventEmitter<void>();

  @ViewChild('heremap') hereMapComponent: HereMapComponent;
  @ViewChild('mapContainer') mapContainerEl: ElementRef;

  location?: ILatLon;
  coordinates: ILatLon;

  @Input() pins: ILatLon[] = [];
  @Input() center: ILatLon;
  @Input() text: string = 'Verify the location for the guest';
  @Input() draggable: boolean = true;

  zoom = 16;

  get isNative() {
    return Capacitor.isNativePlatform();
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private loadingController: LoadingController,
    private alertFacade: AlertFacade,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {}

  async ngOnInit() {
    const existingPin = this.pins?.find((pin) => !!(pin.lat && pin.lon));
    if (this.center && this.center.lat && this.center.lon) {
      this.location = this.center;
      this.cdr.detectChanges();
    } else if (existingPin) {
      this.location = existingPin;
      this.cdr.detectChanges();
    } else {
      const loading = await this.loadingController.create({
        duration: 15000,
        message: 'Finding your current location...',
      });
      await loading.present();
      Geolocation.getCurrentPosition()
        .then(async (location) => {
          await loading.dismiss();
          this.location = {
            lat: location.coords.latitude,
            lon: location.coords.longitude,
          };
          this.buildMap();
          this.cdr.detectChanges();
        })
        .catch(async () => {
          await loading.dismiss();
          this.alertFacade.display({
            level: 'error',
            title: 'Location Not Found',
            body: 'Unable to find your location.',
          });
          this.location = {
            lat: DEFAULT_LAT,
            lon: DEFAULT_LON,
          };
          this.buildMap();
        });

      return;
    }

    this.buildMap();
  }

  showMap(): boolean {
    return Boolean(this.mapContainerEl?.nativeElement?.offsetParent);
  }

  newPosition(coordinates: ILatLon) {
    this.coordinates = coordinates;
    this.locationClicked.emit(coordinates);
  }

  updateCoordinates(coordinates: ILatLon) {
    this.coordinates = coordinates;
  }

  private buildMap() {
    if (!isPlatformServer(this.platformId)) {
      setTimeout(() => this.hereMapComponent.buildMap(), 250);
    }
  }
}
